import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import React, { useCallback, useEffect, useState } from "react";
import tw, { styled, css } from "twin.macro";
import { TabHeader } from "components/TabHeader";
import { UtilityButton } from "components/RFQ/UtilityButton";
import { Card, Flex, Grid, useDisclosure } from "@chakra-ui/react";
import { Heading } from "components/Heading";
import StatusBadge from "components/RFQ/StatusBadge/StatusBadge";
import { AsyncImage } from "loadable-image";
import { Icon } from "assets/Icon";
import NotFoundDatas from "components/RFQ/NotFoundDatas/NotFoundDatas";
import { SupplierList } from "./SupplierList";
import { useDispatch } from "react-redux";
import {
	getProjectById,
	setProjectFavorite,
	getFavoriteProjects,
} from "actions/RFQ/projectsAction";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import SkeletonBase from "newui/components/Skeleton";
import BackButton from "newui/components/BackButton";
import { DEFAULT_IMAGE_BANNER } from "utils/constants";
import { connect } from "react-redux";
import ProjectArchiveModal from "components/Pages/Projects/ProjectArchiveModal/ProjectArchiveModal";
import { CreateRFxSlider } from "newui/features/Rfx/Creation/CreateRFxSlider";
import { ProfileAvatar } from "newui/components/ProfileAvatar/ProfileAvatar";
import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { AddSupplierListDrawer } from "components/Pages/ProjectDetail/AddSupplierListDrawer/AddSupplierListDrawer";

const ProjectDetailContainer = styled.div(() => [
	tw`relative overflow-auto`,
	css`
		.buttons-container {
			gap: 8px;
		}
		.container-content {
			padding-right: 2px;
			padding-left: 2px;
		}
		.img-card {
			aspect-ratio: 4/3;
			object-fit: cover;
		}
		.project-description ul {
			list-style-type: disc;
			margin-left: 20px;
		}

		.project-description ol {
			list-style-type: decimal;
			margin-left: 20px;
		}

		.project-description li {
			margin-bottom: 5px;
		}
	`,
]);

const ProjectDetail = ({
	id,
	archive,
	favorites,
	getFavoriteProjects,
	isLoadingFavorites,
	...props
}) => {
	const dispatch = useDispatch();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isOpenArchiveModal, setIsOpenArchiveModal] = useState();
	const [isCreateRFxDrawerOpen, setIsCreateRFxDrawerOpen] = useState(false);
	// Which supplier list the drawer was opened from; null for the project-level
	// button, which leaves the supplier list for the user to choose.
	const [createRFxSupplierListId, setCreateRFxSupplierListId] = useState(null);
	const [projectData, setProjectData] = useState(null);
	const openCreateRFxDrawer = useCallback((supplierListId = null) => {
		setCreateRFxSupplierListId(supplierListId);
		setIsCreateRFxDrawerOpen(true);
	}, []);
	const [supplierLists, setSupplierLists] = useState([]);
	const [supplierListsWithRFQs, setSupplierListsWithRFQs] = useState([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const [isArchive, setIsArchive] = useState(
		projectData?.status.toUpperCase() === "ARCHIVED"
	);
	const [isFavorite, setIsFavorite] = useState(
		favorites.includes(parseInt(id)) || false
	);
	const [loadProject, setLoadProject] = useState(false);

	const { clientPaths } = useAuthContext();

	useEffect(() => {
		if (!isLoadingFavorites) {
			getFavoriteProjects();
		}
		if (!projectData) {
			setLoadProject(true);
			getProjectById(id)
				.then((data) => {
					if (data) {
						setProjectData(data);
						setLoadProject(false);
					} else {
						setIsNotFound(true);
					}
				})
				.catch((err) => {
					setIsNotFound(true);
					setLoadProject(false);
				});
		}
		if (projectData) {
			setIsArchive(projectData?.status.toUpperCase() === "ARCHIVED");
			setSupplierListsWithRFQs(projectData?.rfqs || []);
			setSupplierLists(projectData?.supplierList || []);
		}
		if (isLoadingFavorites === "idle") {
			setIsFavorite(favorites.includes(parseInt(id)) || false);
		}
	}, [
		projectData,
		id,
		favorites,
		isLoadingFavorites,
		getFavoriteProjects,
		dispatch,
	]);

	const handleFavourite = async (e) => {
		e.stopPropagation(); // Prevent event from double calling

		// Optimistically update UI
		setIsFavorite(!isFavorite);

		try {
			await dispatch(
				setProjectFavorite({
					project_ids: [id],
					isRemove: isFavorite,
				})
			);
			// Refresh favorite projects list
			getFavoriteProjects();
		} catch (error) {
			setIsFavorite(isFavorite);
			console.error("Failed to update favorite status:", error);
		}
	};

	const handleProjectNavigation = () => {
		navigate(clientPaths.rfxProjectsWithType(projectData?.projectType?.slug));
	};

	isNotFound && <NotFound />;

	return isNotFound ? (
		<NotFound />
	) : (
		<ProjectDetailContainer {...props}>
			<TabHeader icon={<BackButton navigateTo={clientPaths.rfxProjectsWithType(projectData?.projectType?.slug)} />}>
				<Flex gap={4}>
					{!loadProject ? (
						<>
							{!isArchive && (
								<>
									<UtilityButton
										text="Archive Project"
										icon="archive"
										onClick={() => setIsOpenArchiveModal(true)}
										data-testid="archive-project-button"
									/>
									<Button
										onClick={() =>
											navigate(
												clientPaths.editRfxProject(id, projectData?.name)
											)
										}
										data-testid="update-project-button"
									>
										<div className="flex items-center gap-1">
											<Icon icon="edit" />
											<span>Update Project/Asset</span>
										</div>
									</Button>
								</>
							)}
						</>
					) : (
						<>
							<SkeletonBase
								width="120px"
								height="40px"
							/>
							<SkeletonBase
								width="120px"
								height="40px"
							/>
						</>
					)}
				</Flex>
			</TabHeader>
			<div className="container-content">
				<Card className="card-shadow relative mb-8 overflow-hidden rounded-2xl bg-gray-50">
					<Grid
						className="grid-cols-12 rounded-md bg-white"
						justifyContent={"space-between"}
					>
						<div className="col-span-7 p-8">
							<div
								className="flex items-center"
								gap={6}
							>
								<ProfileAvatar
									imagePath={projectData?.logoPath}
									isLoading={loadProject}
									className="mr-6 mt-2 size-[50px] p-1 shadow-md"
								/>
								{!loadProject ? (
									<Heading className="text-5xl !text-royal-blue">
										{projectData?.name}
									</Heading>
								) : (
									<SkeletonBase
										height="48px"
										width="70%"
									/>
								)}
							</div>
							<div className="mt-4">
								<AccordionLargeTextDisplay
									isLoading={loadProject}
									maxTextDisplay={250}
									title={"Project Description"}
									text={projectData?.description}
								/>
							</div>
							<div
								id="project-info"
								className="mt-4 border-t pt-6"
							>
								{!loadProject && projectData ? (
									<Flex className="flex-col gap-4 text-base font-semibold">
										<Flex gap={4}>
											<p
												className="min-w-36 font-roboto font-normal text-secondary-500"
												style={{ lineHeight: "24px", fontSize: "16px" }}
											>
												Project Status
											</p>
											<StatusBadge status={projectData?.status} />
										</Flex>
										<Flex gap={4}>
											<p
												className="min-w-36 font-roboto font-normal text-secondary-500"
												style={{ lineHeight: "24px", fontSize: "16px" }}
											>
												Project Sector
											</p>
											<p
												className="font-roboto font-semibold text-primary-900"
												style={{ fontSize: "16px", lineHeight: "24px" }}
											>
												{projectData &&
													projectData.sector
														.map((sector) => sector.name)
														.join(", ")}
											</p>
										</Flex>
										<Flex gap={4}>
											<p
												className="min-w-36 font-roboto font-normal text-secondary-500"
												style={{ lineHeight: "24px", fontSize: "16px" }}
											>
												Project Lead
											</p>
											<p
												className="font-roboto font-semibold text-primary-900"
												style={{ fontSize: "16px", lineHeight: "24px" }}
											>
												{`${projectData?.projectLead?.firstName} ${projectData?.projectLead?.lastName}`}
											</p>
										</Flex>
										<Flex gap={4}>
											<p
												className="min-w-36 font-roboto font-normal text-secondary-500"
												style={{ lineHeight: "24px", fontSize: "16px" }}
											>
												Project Location
											</p>
											<p
												className="font-roboto font-semibold text-primary-900"
												style={{ fontSize: "16px", lineHeight: "24px" }}
											>
												{projectData?.location}
											</p>
										</Flex>
									</Flex>
								) : (
									<Flex className="flex-col gap-4 text-base font-semibold">
										<Flex gap={4}>
											<SkeletonBase />
											<SkeletonBase width="280px" />
										</Flex>
										<Flex gap={4}>
											<SkeletonBase />
											<SkeletonBase width="280px" />
										</Flex>
										<Flex gap={4}>
											<SkeletonBase />
											<SkeletonBase width="280px" />
										</Flex>
										<Flex gap={4}>
											<SkeletonBase />
											<SkeletonBase width="280px" />
										</Flex>
									</Flex>
								)}
							</div>
						</div>
						<div className="relative col-span-5">
							<div className="h-full w-full overflow-hidden rounded-md">
								<AsyncImage
									src={projectData?.backgroundImagePath}
									alt="banner-card"
									className="img-card object-cover"
									loader={
										<img
											src={DEFAULT_IMAGE_BANNER}
											alt="banner-card"
											className="img-card"
											style={{ filter: "blur(10px)" }}
										/>
									}
								/>
							</div>
							<Icon
								icon="favorite"
								style={{
									color: isFavorite ? "#FFCD00" : "transparent",
									alignSelf: "center",
									cursor: "pointer",
									filter: "drop-shadow(0px 0px 1px #EAECF0)",
								}}
								className="absolute right-5 top-5 z-10"
								onClick={handleFavourite}
							/>
						</div>
					</Grid>
				</Card>
				<div id="card-supplier-list">
					<Card className="card-shadow relative mb-8 rounded-md !border !border-gray-50 bg-white p-1">
						<div
							id="card-container-supplier"
							className="bg-white"
						>
							<div
								id="card-header-supplier"
								className="flex items-center justify-between border-b px-6 py-5"
							>
								<div
									gap={2}
									className="flex items-center"
								>
									<h1 className="text-lg font-semibold text-black">
										Supplier List(s) attached to this Project
									</h1>
								</div>
								<div className="flex gap-2">
									<Button
										btntype={"secondary"}
										onClick={() => {
											setCreateRFxSupplierListId(null);
											setIsCreateRFxDrawerOpen(true);
										}}
									>
										Create New RFx
									</Button>
									<Button
										btntype={"primary"}
										onClick={onOpen}
										data-testid="attach-supplier-list-button"
									>
										<div className="flex items-center gap-1">
											<Icon
												icon="plus"
												style={{
													width: "14px",
													height: "14px",
													color: "#fff",
													fontWeight: "bold",
												}}
											/>
											<span>Attach Supplier List</span>
										</div>
									</Button>
								</div>
							</div>
							<div
								id="supplier-content"
								className="rounded-md"
							>
								{supplierListsWithRFQs.length > 0 ? (
									<div>
										<SupplierList
											data={supplierListsWithRFQs}
											project={projectData}
											setSupplierLists={setSupplierLists}
											setSupplierListsWithRFQs={setSupplierListsWithRFQs}
											onCreateRfx={openCreateRFxDrawer}
										/>
									</div>
								) : (
									<div className={`place-content-center`}>
										<NotFoundDatas
											text={"No Supplier Lists found"}
											description={
												"To get started, attach relevant Suppliers from your Supplier List to this project/asset"
											}
											className="absolute left-2/4 -translate-x-2/4"
										>
											<Button
												btntype={"primary"}
												style={{ width: "max-content" }}
												onClick={onOpen}
											>
												<Flex
													gap={2}
													align={"center"}
												>
													<div className="flex h-5 w-5 items-center justify-center">
														<Icon
															icon="plus"
															style={{
																width: "12px",
																height: "12px",
																color: "#fff",
																fontWeight: "bold",
															}}
														/>
													</div>
													Attach Supplier List
												</Flex>
											</Button>
										</NotFoundDatas>
									</div>
								)}
							</div>
						</div>
					</Card>
					{isOpen && (
						<AddSupplierListDrawer
							projectData={projectData}
							defaultLists={supplierLists}
							isOpenContainer={isOpen}
							onCloseContainer={onClose}
							projectId={projectData?.id}
							setSupplierLists={setSupplierLists}
							setSupplierListsWithRFQs={setSupplierListsWithRFQs}
						/>
					)}
					{isCreateRFxDrawerOpen && (
						<CreateRFxSlider
							isOpen={isCreateRFxDrawerOpen}
							onClose={() => {
								setIsCreateRFxDrawerOpen(false);
								setCreateRFxSupplierListId(null);
							}}
							supplierListId={createRFxSupplierListId}
							projectId={projectData.id}
							projectName={projectData.name}
							projectTypeId={projectData?.projectType?.id}
						/>
					)}
				</div>
			</div>
			<ProjectArchiveModal
				isModalOpen={isOpenArchiveModal}
				setIsModalOpen={setIsOpenArchiveModal}
				isArchived={isArchive}
				onSuccessHandler={handleProjectNavigation}
				projectData={projectData}
			/>
		</ProjectDetailContainer>
	);
};

const mapStateToProps = (state) => {
	return {
		favorites: state.rfq.projects.favorites.project_ids,
		isLoadingFavorites: state.rfq.projects.favorites.loading,
	};
};

export default connect(mapStateToProps, { getFavoriteProjects })(ProjectDetail);
